import React from 'react'
import { FormTypes } from '@oneblink/types'

import { useAppConfig } from '../../../providers/AppConfigProvider'
import useSubmitForm from '../../../hooks/useSubmitForm'
import AutoSaveControlledForm from '../../../components/AutoSaveControlledForm'

import {
  findElement,
  replaceElement,
  replaceElementOptions,
} from '../../../lib/definition-manipulation'
import { formElementsService } from '@oneblink/sdk-core'
import {
  conditionallyReadOnly,
  getOptions,
  populateValue,
} from '../../../lib/controlled-forms/meta-functions/meta-functions'
import {
  isConditionallyReadOnlyMeta,
  isPopulateOptionSetMeta,
  isPopulateValueMeta,
} from '../../../lib/narrowing'

type Props = {
  form: FormTypes.Form
  preFillData: Record<string, unknown>
}

export function useCustomFunctions(form: FormTypes.Form) {
  const {
    submissionCustomFunctions,
    definitionCustomFunctions,
    elementNamesSet,
  } = React.useMemo(() => {
    const definitionFunctions = new Map<
      'populateOptionSet' | 'conditionallyReadOnly',
      Array<{ elementName: string; meta: CustomFunctionMeta }>
    >()
    const submissionFunctions = new Map<
      'populateValue',
      Array<{ elementName: string; meta: CustomFunctionMeta }>
    >()
    const namesSet = new Set<string>()
    formElementsService.forEachFormElement(form.elements, (element) => {
      if ('name' in element) {
        namesSet.add(element.name)
        if (element.meta) {
          const meta = JSON.parse(element.meta)

          if ('customFunctions' in meta) {
            const functions = Object.keys(meta.customFunctions)
            functions.forEach((fnName) => {
              console.log(
                `saving custom function ${fnName} for element ${element.name}`,
              )
              switch (fnName) {
                case 'populateOptionSet':
                case 'conditionallyReadOnly': {
                  const existingFunctions =
                    definitionFunctions.get(fnName) || []
                  existingFunctions.push({
                    elementName: element.name,
                    meta: meta.customFunctions[fnName],
                  })
                  definitionFunctions.set(fnName, existingFunctions)
                  break
                }
                case 'populateValue': {
                  const existingFunctions =
                    submissionFunctions.get(fnName) || []
                  existingFunctions.push({
                    elementName: element.name,
                    meta: meta.customFunctions[fnName],
                  })
                  submissionFunctions.set(fnName, existingFunctions)
                  break
                }
                default: {
                  console.warn(
                    `Unknown custom function ${fnName} for element ${element.name}`,
                  )
                }
              }
            })
          }
        }
      }
    })

    return {
      definitionCustomFunctions: definitionFunctions,
      submissionCustomFunctions: submissionFunctions,
      elementNamesSet: namesSet,
    }
  }, [form.elements])

  /* update definition here */
  const updateDefinition = React.useCallback(
    (submission: Record<string, unknown>, definition: FormTypes.Form) => {
      definitionCustomFunctions
        .get('populateOptionSet')
        ?.forEach(({ elementName, meta }) => {
          const targetElement = formElementsService.findFormElement(
            definition.elements,
            (element) =>
              'name' in element ? element.name === elementName : false,
          )
          if (targetElement && isPopulateOptionSetMeta(meta)) {
            const options = getOptions({
              targetElement,
              optionsMeta: meta.dynamicOptions,
              submission,
              definition,
              elementNamesSet,
            })

            replaceElementOptions(definition, elementName, options, [
              ...meta.staticOptions,
            ])
          }
        })

      definitionCustomFunctions
        .get('conditionallyReadOnly')
        ?.forEach(({ elementName, meta }) => {
          const foundElement = findElement(definition, elementName)
          if (!foundElement) {
            return
          }

          if (
            foundElement.parent &&
            foundElement.value &&
            isConditionallyReadOnlyMeta(meta)
          ) {
            const newReadOnlyElement = conditionallyReadOnly({
              definition,
              submission,
              targetElement: foundElement.value,
              meta,
            })
            foundElement.parent.elements = replaceElement(
              foundElement.parent.elements || [],
              foundElement.value,
              newReadOnlyElement,
            )
          }
        })

      // pages are memo'd so we need new objects for the updated definition to render
      return {
        ...definition,
        elements: definition.elements.map((el) => ({ ...el })),
      }
    },
    [definitionCustomFunctions, elementNamesSet],
  )

  /* update submission data here */
  const updateSubmission = React.useCallback(
    (newSubmission: Record<string, unknown>) => {
      if (!submissionCustomFunctions.size) {
        return newSubmission
      }

      const submission = { ...newSubmission }

      submissionCustomFunctions
        .get('populateValue')
        ?.forEach(({ elementName, meta }) => {
          const targetElement = formElementsService.findFormElement(
            form.elements,
            (element) =>
              'name' in element ? element.name === elementName : false,
          )

          if (targetElement && isPopulateValueMeta(meta)) {
            populateValue({
              targetElement,
              sourceMeta: meta,
              definition: form,
              submission,
            })
          }
        })
      return submission
    },
    [form, submissionCustomFunctions],
  )

  return {
    updateDefinition,
    updateSubmission,
  }
}

export default function GenericControlledFormContainer({
  form,
  preFillData,
}: Props) {
  const { buttons } = useAppConfig()

  const { updateDefinition, updateSubmission } = useCustomFunctions(form)

  const [
    { onSubmit, clearServerValidationMessage },
    { isSubmitting, serverValidationMessage },
  ] = useSubmitForm({
    formDefinition: form,
    preFillData,
    skipServerValidation: false,
  })

  return (
    <AutoSaveControlledForm
      form={form}
      initialSubmission={preFillData}
      onSubmit={onSubmit}
      buttons={buttons}
      updateSubmission={updateSubmission}
      updateDefinition={updateDefinition}
      isSubmitting={isSubmitting}
      serverValidationMessage={serverValidationMessage}
      clearServerValidationMessage={clearServerValidationMessage}
    />
  )
}
